import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faArrowLeft, faFilter, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "31px",
    height: "31px",
    fontSize: "13px",
    textAlign: "left",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "31px",
    textAlign: "left",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "31px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    textAlign: "left",
  }),
  option: (base) => ({
    ...base,
    textAlign: "left",
    fontSize: "13px",
  }),
};

export default function ReminderCustomerSummary() {
  const { http, user } = AuthUser();
  const navigate = useNavigate();
  const isSuperAdmin = user && (user.id == 1 || user.id == 3);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reminderTypes, setReminderTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("active");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/jobcart/reminder");
      return;
    }
    http.get("/reminder_types").then((res) => {
      if (res.data.success) setReminderTypes(res.data.data || []);
    });
    http.get(`/unique_reminder_company?login_user=${user.id}`).then((res) => {
      setCompanies(res.data.data || []);
    });
    fetchData("", "active", "", "", "");
  }, []);

  const fetchData = (
    type = "",
    statusFilter = "active",
    company = "",
    from = "",
    to = ""
  ) => {
    setIsLoading(true);
    http
      .get(
        `/reminder?status=${type}&company=${company}&startdate=${from}&enddate=${to}&status_filter=${statusFilter}`
      )
      .then((res) => {
        setIsLoading(false);
        if (Array.isArray(res.data.data)) setTasks(res.data.data);
      })
      .catch(() => setIsLoading(false));
  };

  const companyOptions = [
    ...new Map(
      companies
        .filter((c) => c.company_name)
        .map((c) => {
          const name = c.company_name.trim();
          return [name.toLowerCase(), { value: name, label: name }];
        })
    ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const handleFilter = () => {
    fetchData(
      selectedType,
      selectedStatusFilter,
      selectedCompany,
      startDate,
      endDate
    );
  };

  const handleClear = () => {
    setSelectedType("");
    setSelectedStatusFilter("active");
    setSelectedCompany("");
    setStartDate("");
    setEndDate("");
    fetchData("", "active", "", "", "");
  };

  const buildSummary = () => {
    const grouped = {};
    tasks.forEach((c) => {
      const company = (c.company_name || "Unknown").trim() || "Unknown";
      if (!grouped[company]) {
        grouped[company] = {
          company,
          count: 0,
          total_system: 0,
          total_rate: 0,
          total_amount: 0,
        };
      }
      grouped[company].count += 1;
      grouped[company].total_system += Number(c.total_system) || 0;
      grouped[company].total_rate += Number(c.rate) || 0;
      grouped[company].total_amount += Number(c.amount) || 0;
    });
    return Object.values(grouped).sort((a, b) =>
      a.company.localeCompare(b.company)
    );
  };

  const summaryRows = buildSummary();
  const grandCount = tasks.length;
  const grandSystem = summaryRows.reduce((s, r) => s + r.total_system, 0);
  const grandRate = summaryRows.reduce((s, r) => s + r.total_rate, 0);
  const grandAmount = summaryRows.reduce((s, r) => s + r.total_amount, 0);

  const fmt = (n) => Number(n).toLocaleString("en-IN");

  const activeFilterCount = [
    selectedType,
    selectedStatusFilter !== "active" ? selectedStatusFilter : "",
    selectedCompany,
    startDate,
    endDate,
  ].filter(Boolean).length;

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setTextColor(13, 110, 253);
    doc.text("Reminder Customer Summary", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`, 14, 25);
    doc.text("Grouped by Company", 14, 30);

    autoTable(doc, {
      startY: 36,
      head: [["Company", "Count", "Total System", "Total Rate", "Total Amount"]],
      body: [
        ...summaryRows.map((r) => [
          r.company,
          r.count,
          r.total_system,
          fmt(r.total_rate),
          fmt(r.total_amount),
        ]),
        ["TOTAL", grandCount, grandSystem, fmt(grandRate), fmt(grandAmount)],
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 246, 255] },
      didParseCell: (data) => {
        if (data.row.index === summaryRows.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 235, 255];
          data.cell.styles.textColor = [0, 0, 0];
        }
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    doc.save(`reminder_customer_summary_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-bold">Remind Customer Summary</h4>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            Grouped by Company &mdash; {tasks.length} total records
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={downloadPDF}
            disabled={summaryRows.length === 0}
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-1" /> Download PDF
          </button>
        </div>
      </div>

      <div
        className="mb-3"
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center flex-wrap gap-2"
          style={{ padding: "8px 12px", background: "#f8fafc" }}
        >
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <FontAwesomeIcon icon={faFilter} className="me-1" />
              Filters
              {activeFilterCount > 0 && (
                <span className="badge bg-primary ms-1">{activeFilterCount}</span>
              )}
              <FontAwesomeIcon
                icon={showFilters ? faChevronUp : faChevronDown}
                className="ms-2"
              />
            </button>
            {!showFilters && (
              <span className="text-muted" style={{ fontSize: "12px" }}>
                Click Filters to refine report
              </span>
            )}
          </div>
          <span
            className="fw-bold"
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              borderRadius: "999px",
              padding: "4px 12px",
              fontSize: "13px",
            }}
          >
            Total Amount: ₹ {fmt(grandAmount)} ({summaryRows.length} companies)
          </span>
        </div>

        {showFilters && (
          <div style={{ padding: "12px 14px", borderTop: "1px solid #e5e7eb" }}>
            <div className="row g-2 align-items-end">
              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                  Type
                </label>
                <select
                  className="form-control form-control-sm"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="">All Type</option>
                  {reminderTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                  Status
                </label>
                <select
                  className="form-control form-control-sm"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="active">NEW & RE-NEW</option>
                  <option value="NEW">NEW</option>
                  <option value="RE-NEW">RE-NEW</option>
                  <option value="LEFT">LEFT</option>
                  <option value="STOPPED">STOPPED</option>
                  <option value="all">All Status</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                  Company
                </label>
                <Select
                  options={companyOptions}
                  value={
                    companyOptions.find((o) => o.value === selectedCompany) || null
                  }
                  onChange={(opt) => setSelectedCompany(opt ? opt.value : "")}
                  placeholder="Search company..."
                  isClearable
                  isSearchable
                  styles={selectStyles}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                  From Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label mb-1" style={{ fontSize: "12px" }}>
                  To Date
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary btn-sm px-3"
                onClick={() => {
                  handleFilter();
                  setShowFilters(false);
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-3"
                onClick={() => {
                  handleClear();
                  setShowFilters(false);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <TaskSkeleton />
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered" style={{ maxWidth: "900px" }}>
            <thead style={{ backgroundColor: "#0d6efd", color: "#fff" }}>
              <tr>
                <th>Company</th>
                <th className="text-center">Count</th>
                <th className="text-end">Total System</th>
                <th className="text-end">Total Rate</th>
                <th className="text-end">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No data available
                  </td>
                </tr>
              ) : (
                <>
                  {summaryRows.map((r, i) => (
                    <tr
                      key={r.company}
                      style={{ backgroundColor: i % 2 === 0 ? "#f0f6ff" : "#fff" }}
                    >
                      <td
                        className="fw-medium"
                        title={r.company}
                        style={{
                          maxWidth: "280px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.company}
                      </td>
                      <td className="text-center">{r.count}</td>
                      <td className="text-end">{r.total_system}</td>
                      <td className="text-end">{fmt(r.total_rate)}</td>
                      <td className="text-end">{fmt(r.total_amount)}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#dce7ff", fontWeight: "bold" }}>
                    <td>TOTAL</td>
                    <td className="text-center">{grandCount}</td>
                    <td className="text-end">{grandSystem}</td>
                    <td className="text-end">{fmt(grandRate)}</td>
                    <td className="text-end">{fmt(grandAmount)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
